import React, { useState, useEffect, useRef } from 'react';
import { DOMAIN } from '@/config';
import ContentCard from '@/components/ui/ContentCard';
import Reveal from '@/components/ui/Reveal';
import { popularSearches } from '@/constants';
import { MagnifyingGlassIcon } from '@/components/ui/Icons';

interface SearchBarProps {
    id: string;
}

const PLACEHOLDER_TEXTS = [
    "Search for Movies, TV Shows...",
    "e.g.: Iron Man",
    "e.g.: The Boys",
    "e.g.: Game of Thrones",
    "e.g.: The Witcher",
    "e.g.: Avatar",
];

const TYPING_SPEED = 60;
const DELETING_SPEED = 30;
const PAUSE_DURATION = 2000;

const SearchBar = React.forwardRef<HTMLElement, SearchBarProps>(({ id }, ref) => {
    const [placeholder, setPlaceholder] = useState('');
    const [textIndex, setTextIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Blinking cursor effect
    useEffect(() => {
        const interval = setInterval(() => {
            setShowCursor(prev => !prev);
        }, 500);
        return () => clearInterval(interval);
    }, []);

    // Typing loop
    useEffect(() => {
        const currentText = PLACEHOLDER_TEXTS[textIndex];

        if (!isDeleting && charIndex === currentText.length) {
            // Pause when complete before starting delete
            const timeout = setTimeout(() => {
                setIsDeleting(true);
            }, PAUSE_DURATION);
            return () => clearTimeout(timeout);
        }

        if (isDeleting && charIndex === 0) {
            // Done deleting, move to next text
            setIsDeleting(false);
            setTextIndex(prev => (prev + 1) % PLACEHOLDER_TEXTS.length);
            return;
        }

        const nextCharIndex = charIndex + (isDeleting ? -1 : 1);
        const speed = isDeleting ? DELETING_SPEED : TYPING_SPEED;

        const timeout = setTimeout(() => {
            setCharIndex(nextCharIndex);
            setPlaceholder(currentText.substring(0, nextCharIndex));
        }, speed);

        return () => clearTimeout(timeout);
    }, [charIndex, isDeleting, textIndex]);

    const handlePopularSearchClick = (keyword: string) => {
        setInputValue(keyword);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <Reveal as="section" ref={ref} id={id} className="mb-12 md:mb-16">
            <ContentCard className="rounded-3xl sm:rounded-[2.25rem] p-6 sm:p-10">
                <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center">Find Your Favorite Movies & Shows</h2>

                <form id="search-form" className="relative max-w-2xl mx-auto mb-4" action={`https://${DOMAIN}`} method="get" target="_blank">
                    <input
                        ref={inputRef}
                        type="text"
                        name="s"
                        id="search-input"
                        role="searchbox"
                        aria-label="Search for movies and TV shows"
                        autoComplete="off"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="w-full py-4 px-6 pr-14 bg-slate-900/5 dark:bg-[var(--button-bg)] border-2 border-slate-300 dark:border-[var(--card-border)] rounded-full text-[var(--main-text)] placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/50 transition duration-300 shadow-none"
                        placeholder={inputValue ? '' : placeholder + (showCursor ? '|' : '')}
                        required
                    />
                    <button type="submit" aria-label="Search" className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-orange-500 to-red-500 w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-105 transition-transform cursor-pointer">
                        <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                </form>

                <div className="flex flex-wrap justify-center items-center gap-2 mt-6 text-sm" suppressHydrationWarning>
                    <span className="text-slate-400 mr-2 font-medium">Popular Searches:</span>
                    {popularSearches.map(keyword => (
                        <button 
                            key={keyword} 
                            type="button" 
                            onClick={() => handlePopularSearchClick(keyword)}
                            className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[rgba(229,9,20,0.06)] border border-[rgba(229,9,20,0.12)] text-red-700 dark:text-red-400 hover:bg-[rgba(229,9,20,0.12)] dark:hover:bg-[rgba(229,9,20,0.18)] transition-all duration-300 cursor-pointer"
                        >
                            {keyword}
                        </button>
                    ))}
                </div>
            </ContentCard>
        </Reveal>
    );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;